import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useSelector, useDispatch } from 'react-redux';
import { selectDefaultFamilySize, setDefaultFamilySize } from '../../redux/userPreferencesSlice';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';

interface FamilySizeSelectorProps {
  compact?: boolean; // Inline mode for grocery list header
}

export const FamilySizeSelector: React.FC<FamilySizeSelectorProps> = ({
  compact = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const familySize = useSelector(selectDefaultFamilySize);

  const handleChange = (delta: number) => {
    dispatch(setDefaultFamilySize(familySize + delta));
  };

  if (compact) {
    return (
      <View style={styles.compactContainer}>
        <TouchableOpacity
          style={styles.compactButton}
          onPress={() => handleChange(-1)}
          disabled={familySize <= 1}
          activeOpacity={0.6}
        >
          <Feather name="minus" size={14} color={familySize <= 1 ? Paprika.textMuted : Paprika.textPrimary} />
        </TouchableOpacity>
        <View style={styles.compactValue}>
          <Feather name="users" size={14} color={Paprika.accent} />
          <Text style={styles.compactValueText}>{familySize}</Text>
        </View>
        <TouchableOpacity
          style={styles.compactButton}
          onPress={() => handleChange(1)}
          disabled={familySize >= 12}
          activeOpacity={0.6}
        >
          <Feather name="plus" size={14} color={familySize >= 12 ? Paprika.textMuted : Paprika.textPrimary} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        <Feather name="users" size={18} color={Paprika.accent} />
        <Text style={styles.label}>{t('settings.defaultServings')}</Text>
      </View>
      <Text style={styles.description}>
        {t('settings.defaultServingsDescription')}
      </Text>
      <View style={styles.selectorRow}>
        <TouchableOpacity
          style={[styles.button, familySize <= 1 && styles.buttonDisabled]}
          onPress={() => handleChange(-1)}
          disabled={familySize <= 1}
          activeOpacity={0.6}
        >
          <Feather name="minus" size={18} color={familySize <= 1 ? Paprika.textMuted : Paprika.textPrimary} />
        </TouchableOpacity>
        <View style={styles.valueContainer}>
          <Text style={styles.valueText}>{familySize}</Text>
          <Text style={styles.valueLabel}>{t('settings.servings')}</Text>
        </View>
        <TouchableOpacity
          style={[styles.button, familySize >= 12 && styles.buttonDisabled]}
          onPress={() => handleChange(1)}
          disabled={familySize >= 12}
          activeOpacity={0.6}
        >
          <Feather name="plus" size={18} color={familySize >= 12 ? Paprika.textMuted : Paprika.textPrimary} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Full mode
  container: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    marginBottom: 4,
  },
  label: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  description: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.md,
  },
  selectorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.lg,
  },
  button: {
    width: 40,
    height: 40,
    borderRadius: PaprikaRadius.full,
    borderWidth: 1,
    borderColor: Paprika.border,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLight,
  },
  buttonDisabled: {
    opacity: 0.4,
  },
  valueContainer: {
    alignItems: 'center',
    minWidth: 60,
  },
  valueText: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.accent,
  },
  valueLabel: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
  },

  // Compact mode
  compactContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Paprika.surfaceLight,
    borderRadius: PaprikaRadius.xl,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  compactButton: {
    width: 24,
    height: 24,
    borderRadius: PaprikaRadius.full,
    justifyContent: 'center',
    alignItems: 'center',
  },
  compactValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  compactValueText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
    fontWeight: '600',
  },
});

export default FamilySizeSelector;
